import classNames from 'classnames';
import { useMemo } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_TYPES } from '@api/instant_survey';
import type { Question } from '@api/instant_survey/types';
import { GATES } from '@constants';
import { OverlayTriggerTooltip } from '@utilities';

import styles from './_index.module.scss';

interface SidebarProps {
  allowedQuestionTypes?: Array<{
    description: string;
    icon: React.ReactNode;
    name: string;
    value: string;
  }>;
  canSelectQuestionType?: boolean;
  isVideoSurvey: boolean;
  onSelectQuestionType: (type: string) => void;
  question: Question;
}

const Sidebar = ({
  allowedQuestionTypes,
  canSelectQuestionType = true,
  isVideoSurvey,
  onSelectQuestionType,
  question,
}: SidebarProps) => {
  const { client } = useStatsigClient();
  const canAIInterview = client.checkGate(GATES.QUESTION_TYPE_AI_INTERVIEW);
  const canVanWestendorp = client.checkGate(GATES.VAN_WESTENDORP);
  const reorganizedQuestionTypes = client.checkGate(GATES.REORGANIZED_QUESTION_TYPES);
  const requireQuestionTypeSelection = client.checkGate(GATES.REQUIRE_QUESTION_TYPE_SELECTION);

  const isQuestionTypeAllowed = (type: { value: string }) =>
    !allowedQuestionTypes ||
    allowedQuestionTypes.some((allowedType) => allowedType.value === type.value);

  const additionalQuestionTypes = useMemo(() => {
    if (reorganizedQuestionTypes) {
      return [
        QUESTION_TYPES['RANK-DRAGDROP'],
        QUESTION_TYPES.CONTSUM,
        QUESTION_TYPES.NUMERIC,
      ].filter(isQuestionTypeAllowed);
    }
    return [
      ...(canVanWestendorp ? [QUESTION_TYPES.VAN_WESTENDORP] : []),
      QUESTION_TYPES.INSTRUCTIONS,
      QUESTION_TYPES['RANK-DRAGDROP'],
      QUESTION_TYPES['TABLE-RADIO'],
      QUESTION_TYPES.TURF,
      QUESTION_TYPES.CONTSUM,
      QUESTION_TYPES.MAX_DIFF,
      QUESTION_TYPES.NUMERIC,
    ].filter(isQuestionTypeAllowed);
  }, [allowedQuestionTypes, canVanWestendorp, reorganizedQuestionTypes]);
  const advancedQuestionTypes = useMemo(() => {
    if (!reorganizedQuestionTypes) {
      return [];
    }
    return [
      QUESTION_TYPES.TURF,
      QUESTION_TYPES.MAX_DIFF,
      ...(canVanWestendorp ? [QUESTION_TYPES.VAN_WESTENDORP] : []),
    ].filter(isQuestionTypeAllowed);
  }, [allowedQuestionTypes, canVanWestendorp, reorganizedQuestionTypes]);
  const commonQuestionTypes = useMemo(() => {
    if (reorganizedQuestionTypes) {
      return [
        QUESTION_TYPES.RADIO,
        QUESTION_TYPES.CHECKBOX,
        QUESTION_TYPES.ESSAY,
        QUESTION_TYPES['TABLE-RADIO'],
        QUESTION_TYPES.INSTRUCTIONS,
      ].filter(isQuestionTypeAllowed);
    }
    return [
      ...(requireQuestionTypeSelection
        ? [QUESTION_TYPES.RADIO, QUESTION_TYPES.CHECKBOX]
        : [QUESTION_TYPES.CHECKBOX, QUESTION_TYPES.RADIO]),
      QUESTION_TYPES.ESSAY,
    ].filter(isQuestionTypeAllowed);
  }, [allowedQuestionTypes, reorganizedQuestionTypes, requireQuestionTypeSelection]);
  const qualQuestionTypes = useMemo(() => {
    return canAIInterview ? [QUESTION_TYPES.AI_INTERVIEW].filter(isQuestionTypeAllowed) : [];
  }, [allowedQuestionTypes, canAIInterview]);

  const renderQuestionTypeButton = (type: {
    description: string;
    icon: React.ReactNode;
    name: string;
    value: string;
  }) => {
    return (
      <OverlayTriggerTooltip
        content={type.value === question.type || !canSelectQuestionType ? null : type.description}
        placement="right"
      >
        <button
          aria-pressed={type.value === question.type}
          className={classNames({
            [styles['is-active']]: type.value === question.type,
          })}
          data-testid={type.value}
          disabled={!canSelectQuestionType}
          onClick={() => onSelectQuestionType(type.value)}
        >
          {type.icon}
          <span>{type.name}</span>
        </button>
      </OverlayTriggerTooltip>
    );
  };

  return (
    <div className={styles['sidebar']}>
      <h4>Common Question Types</h4>
      {isVideoSurvey ? (
        <ul data-testid="video-question-types">
          {isQuestionTypeAllowed(QUESTION_TYPES.VIDEO) && (
            <li key={QUESTION_TYPES.VIDEO.value}>
              {renderQuestionTypeButton(QUESTION_TYPES.VIDEO)}
            </li>
          )}
        </ul>
      ) : (
        <>
          <ul data-testid="common-question-types">
            {commonQuestionTypes.map((type) => (
              <li key={type.value}>{renderQuestionTypeButton(type)}</li>
            ))}
          </ul>
          <h4>Additional Question Types</h4>
          <ul data-testid="additional-question-types">
            {additionalQuestionTypes.map((type) => (
              <li key={type.value}>{renderQuestionTypeButton(type)}</li>
            ))}
          </ul>
          {qualQuestionTypes.length > 0 && (
            <>
              <h4>Qual</h4>
              <ul data-testid="qual-question-types">
                {qualQuestionTypes.map((type) => (
                  <li key={type.value}>{renderQuestionTypeButton(type)}</li>
                ))}
              </ul>
            </>
          )}
          {advancedQuestionTypes.length > 0 && (
            <>
              <h4>Advanced Question Types</h4>
              <ul data-testid="advanced-question-types">
                {advancedQuestionTypes.map((type) => (
                  <li key={type.value}>{renderQuestionTypeButton(type)}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Sidebar;
